// DynamicLayoutComponents.js
import React from "react";
import Input from "../../Controls/Input";

// Layout Components
export const Section = ({ children, style = {} }) => (
  <div style={Object.assign({ marginBottom: "24px" }, style)}>{children}</div>
);

export const SectionHeader = ({ title, description }) => (
  <div style={{ marginBottom: "24px" }}>
    <h2 style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "8px" }}>
      {title}
    </h2>
    {description && (
      <p style={{ color: "#6b7280", fontSize: "14px" }}>{description}</p>
    )}
  </div>
);

export const Grid = ({ children, columns = 3, gap = "24px" }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
      gap: gap,
      width: "100%",
    }}
  >
    {children}
  </div>
);

export const Card = ({ children, onMouseEnter, onMouseLeave, style = {} }) => (
  <div
    style={Object.assign({
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "20px",
      background: "#fff",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      transition: "box-shadow 0.3s ease, transform 0.2s ease",
      position: "relative",
      overflow: "hidden",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }, style)}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </div>
);

export const CardHeader = ({ icon, title, subtitle, badge }) => {
  const getBadgeColor = function(badgeText) {
    if (badgeText && badgeText.indexOf("Configured") !== -1) {
      return "#ecfdf5";
    }
    return "#f3f4f6";
  };

  const getBadgeTextColor = function(badgeText) {
    if (badgeText && badgeText.indexOf("Configured") !== -1) {
      return "#059669";
    }
    return "#d97706";
  };

  const getBadgeBorderColor = function(badgeText) {
    if (badgeText && badgeText.indexOf("Configured") !== -1) {
      return "#a7f3d0";
    }
    return "#fde68a";
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "16px",
        paddingBottom: "16px",
        borderBottom: "1px solid #f3f4f6",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            backgroundColor: getBadgeColor(badge),
            marginRight: "16px",
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: "18px", fontWeight: "bold", margin: 0, color: "#111827" }}>
            {title}
          </h3>
          {subtitle && (
            <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0 0" }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {badge && (
        <span
          style={{
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: "600",
            borderRadius: "9999px",
            backgroundColor: getBadgeColor(badge),
            color: getBadgeTextColor(badge),
            border: "1px solid " + getBadgeBorderColor(badge),
            flexShrink: 0,
            marginLeft: "8px",
          }}
        >
          {badge}
        </span>
      )}
    </div>
  );
};

export const WalletBalance = ({ balance, currency = "₹" }) => (
  <div
    style={{
      marginBottom: "16px",
      padding: "12px",
      backgroundColor: "#eff6ff",
      borderRadius: "8px",
      border: "1px solid #bfdbfe",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <div>
      <p style={{ fontSize: "12px", color: "#4b5563", margin: 0 }}>
        Wallet Balance
      </p>
      <p
        style={{
          fontSize: "20px",
          fontWeight: "bold",
          color: "#2563eb",
          margin: "4px 0 0 0",
        }}
      >
        {currency}
        {balance.toLocaleString()}
      </p>
    </div>
    <span style={{ fontSize: "24px" }}>💰</span>
  </div>
);

export const ToggleSection = ({ isActive, onToggle, showToggle = true }) => (
  <div
    style={{
      marginBottom: "20px",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid",
      borderColor: isActive ? "#a7f3d0" : "#d1d5db",
      backgroundColor: isActive ? "#ecfdf5" : "#f9fafb",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
    }}
  >
    <div style={{ flex: 1 }}>
      {isActive ? (
        <React.Fragment>
          <span
            style={{ fontSize: "14px", fontWeight: "600", color: "#065f46" }}
          >
            ✅ Currently Active
          </span>
       
        </React.Fragment>
      ) : (
        <React.Fragment>
          <span
            style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}
          >
            Want to activate?
          </span>
         
        </React.Fragment>
      )}
    </div>
    {showToggle && (
      <button
        onClick={onToggle}
        style={{
          position: "relative",
          display: "inline-flex",
          height: "24px",
          width: "44px",
          alignItems: "center",
          borderRadius: "9999px",
          transition: "background-color 0.3s ease",
          backgroundColor: isActive ? "#10b981" : "#9ca3af",
          border: "none",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            display: "inline-block",
            height: "18px",
            width: "18px",
            transform: isActive ? "translateX(20px)" : "translateX(4px)",
            borderRadius: "9999px",
            backgroundColor: "#ffffff",
            transition: "transform 0.3s ease",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          }}
        />
      </button>
    )}
  </div>
);

export const Button = ({ children, onClick, variant = "primary", disabled = false, icon = null }) => {
  const getButtonStyles = function() {
    if (variant === "primary") {
      return {
        backgroundColor: "#ffffff",
        color: "#2563eb",
        border: "1px solid #2563eb",
      };
    } else if (variant === "secondary") {
      return {
        backgroundColor: "#ffffff",
        color: "#374151",
        border: "1px solid #d1d5db",
      };
    } else if (variant === "danger") {
      return {
        backgroundColor: "#ffffff",
        color: "#dc2626",
        border: "1px solid #dc2626",
      };
    } else {
      return {
        backgroundColor: "#ffffff",
        color: "#2563eb",
        border: "1px solid #2563eb",
      };
    }
  };

  const handleMouseEnter = function(e) {
    if (!disabled) {
      if (variant === "primary") {
        e.target.style.backgroundColor = "#eff6ff";
      } else if (variant === "secondary") {
        e.target.style.backgroundColor = "#f9fafb";
      } else if (variant === "danger") {
        e.target.style.backgroundColor = "#fee2e2";
      }
    }
  };

  const handleMouseLeave = function(e) {
    if (variant === "primary") {
      e.target.style.backgroundColor = "#ffffff";
    } else if (variant === "secondary") {
      e.target.style.backgroundColor = "#ffffff";
    } else if (variant === "danger") {
      e.target.style.backgroundColor = "#ffffff";
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={Object.assign({
        width: "100%",
        padding: "10px 16px",
        borderRadius: "8px",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }, getButtonStyles())}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
};

export const ButtonGroup = ({ children, direction = "column" }) => (
  <div
    style={{
      marginTop: "auto",
      paddingTop: "16px",
      display: "flex",
      flexDirection: direction,
      gap: "8px",
    }}
  >
    {children}
  </div>
);

export const FormField = ({ children, label }) => (
  <div style={{ marginBottom: "16px" }}>
    {label && (
      <label
        style={{
          display: "block",
          fontSize: "14px",
          fontWeight: "500",
          marginBottom: "8px",
          color: "#374151",
        }}
      >
        {label}
      </label>
    )}
    {children}
  </div>
);

export const LoadingSpinner = function() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "160px",
      }}
    >
      <span style={{ color: "#4B5563", fontSize: "18px" }}>Loading...</span>
    </div>
  );
};

// Reusable Integration Card Component
export const IntegrationCard = function({
  icon,
  title,
  subtitle,
  badge,
  walletBalance,
  isActive,
  onToggleActive,
  showToggle = true,
  children,
  actions,
  onMouseEnter,
  onMouseLeave,
}) {
  return (
    <Card onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <CardHeader icon={icon} title={title} subtitle={subtitle} badge={badge} />
      
      {walletBalance !== null && walletBalance !== undefined && <WalletBalance balance={walletBalance} />}
      
      <div style={{ flex: 1 }}>
        {children}
      </div>
      
      {showToggle && (
        <ToggleSection isActive={isActive} onToggle={onToggleActive} />
      )}
      
      {actions && actions.length > 0 && <ButtonGroup>{actions}</ButtonGroup>}
    </Card>
  );
};